import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogTrigger, Popover } from 'react-aria-components';
import { UseFormRegisterReturn } from 'react-hook-form';
import { Icon } from '../atoms';
import { CheckBox } from './Checkbox';
import { Tag } from './Tag';
import { Placement } from 'react-aria';

export type MultiSelectCategory = {
  name: string;
  options: string[];
};

type MultiSelectCategoryDropdownProps = {
  categories: MultiSelectCategory[];
  onSelectionChange: (selectedItems: string[]) => void;
  placeholder?: string;
  selectedItems: string[];
  label?: string|React.ReactNode;
  subLabel?: string|React.ReactNode;
  error?: string;
  selectionLimit?: number;
  placement?: Placement,
  mobile?: boolean,

  register?: () => UseFormRegisterReturn<string>;
};

export const MultiSelectCategoryDropdown = (props: MultiSelectCategoryDropdownProps) => {
  const {
    selectionLimit,
    categories,
    onSelectionChange,
    placeholder,
    selectedItems,
    label,
    subLabel,
    error,
    register,
    placement,
    mobile,
  } = props;
  const [expanded, setExpanded] = useState(-1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const firstCategoryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (dropdownOpen) {
      firstCategoryRef.current?.focus();
    }
  }, [dropdownOpen]);

  const toggleCategory = (index: number) => {
    setExpanded(expanded === index ? -1 : index);
  };

  const handleCheckboxChange = (item: string) => {
    if (selectedItems.includes(item)) {
      onSelectionChange(selectedItems.filter((i) => i !== item));
    } else if (!selectionLimit || selectedItems.length < selectionLimit) {
      onSelectionChange([...selectedItems, item]);
    }
  };

  const handleItemRemove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, item: string) => {
    onSelectionChange(selectedItems.filter((i) => i !== item));
  };

  return (
    <DialogTrigger>
      <div
        className='w-full mx-auto flex flex-col justify-center gap-1 shrink-0 max-h-[596px] overflow-auto '
        {...(register ? register() : {})}
      >
        <div className='w-full flex flex-col gap-2 justify-center items-start text-left'>
          <div className='text-sm font-medium text-gray-700'>{label}</div>
          {subLabel &&  <div className='text-sm text-gray-500'>{subLabel}</div>}
          <Button
            name={'multi-select-category-dropdown'}
            className={'w-full'}
            onPress={() => {
              setDropdownOpen(!dropdownOpen);
            }}
          >
            <div
              className='w-full min-h-[42px] px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm text-gray-700 cursor-pointer flex justify-between items-center overflow-hidden text-ellipsis whitespace-nowrap'
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <span className='overflow-hidden text-ellipsis whitespace-nowrap flex flex-wrap gap-1'>
                {selectedItems.length > 0 ?
                  selectedItems.map((item) => {
                    return (
                      <Tag
                        key={item}
                        size='sm'
                        tailIcon='x-close'
                        onTailIconClick={(event) => handleItemRemove(event, item)}
                      >
                        <div>{item}</div>
                      </Tag>
                    );
                  })
                  : <div className='text-gray-400'>{placeholder}</div>}{' '}
              </span>
              {dropdownOpen ?
                <Icon type='chevron-up' size={'18'} />
                : <Icon type='chevron-down' size={'18'} />}
            </div>
          </Button>
        </div>
        <Popover
          placement={placement ? placement : 'bottom'}
          className={`${mobile ? 'w-10/12' : 'w-[320px]'} md:w-[460px] rounded-md border border-gray-100 bg-white`}
          offset={4}
        >
          <Dialog className='w-full h-[min(50vh,420px)] border-1 border-gray-100 shadow-lg overflow-y-auto'>
            <div className='rounded-md bg-white overflow-y-auto'>
              {categories.map((category, index) => (
                <div key={category.name} className='border-b min-h-12 w-full'>
                  <button
                    ref={index === 0 ? firstCategoryRef : undefined}
                    type='button'
                    onClick={() => toggleCategory(index)}
                    className={`${expanded === index ? 'border-b' : ''} w-full px-4 py-1 h-12 flex justify-between items-center text-left text-sm font-medium text-gray-500 bg-gray-50 hover:bg-gray-100`}
                  >
                    {category.name}
                    {expanded === index ?
                      <Icon type='chevron-up' size={'16'} />
                      : <Icon type='chevron-down' size={'16'} />}
                  </button>
                  {expanded === index && (
                    <div className='px-1 py-0.5'>
                      {category.options.map((option) => {
                        const disabled =
                          !selectedItems.includes(option) &&
                          selectionLimit &&
                          selectedItems.length >= selectionLimit;
                        return (
                          <div key={option} className='p-2 flex items-center min-h-10 bg-white'>
                            <CheckBox
                              id={option}
                              isSelected={selectedItems.includes(option)}
                              size='md'
                              onChange={() => handleCheckboxChange(option)}
                            >
                              <div className='text-sm text-gray-500 font-normal mt-0.5 ml-0.5'>
                                {option}
                                {disabled && (
                                  <span className='text-xs text-error-500 ml-2'>
                                    (Limit reached)
                                  </span>
                                )}
                              </div>
                            </CheckBox>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Dialog>
        </Popover>
        {error && <div className='text-sm text-error-500 p-1'>{error}</div>}
      </div>
    </DialogTrigger>
  );
};
